import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHDate from "_shared/dates/date";
import AssistantResponse from "ai-assistant/assistant-response";
import AssistantCitation from "ai-assistant/citations/assistant-citation";
import AssistantConversationSchema, {
  AssistantConversationJSON,
  AssistantConversationParams,
  AssistantConversationTurnParams,
} from "ai-assistant/conversations/assistant-conversation-schema";
import { Role } from "ai-assistant/roles/assistant-role";
import { AssistantResponseStatus } from "ai-assistant/statuses/assistant-response-status";

/**
 * One of the user's own assistant conversations, as the server holds it.
 */
export default class AssistantConversation {
  public static create(values: AssistantConversationParams): AssistantConversation {
    const parsed = AssistantConversationSchema.validate(values);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Invalid AssistantConversation parameters.");
    }
    return new AssistantConversation(parsed);
  }

  public static fromJSON(json: AssistantConversationJSON): AssistantConversation {
    if (isNullOrUndefined(json)) return json;
    return new AssistantConversation(AssistantConversationSchema.deserialize(json));
  }

  public readonly conversationId: string;
  public readonly startedAt: AHDate;
  public readonly lastMessageAt: AHDate;
  public readonly turns: AssistantConversationTurnParams[];
  /**
   * Whether the assistant prepared anything for approval while this conversation was live.
   *
   * The previews themselves do not come back with it - see `toTranscript` - so this is the
   * only thing that distinguishes a transcript whose answers point at controls that are gone
   * from one that never had any.
   */
  public readonly hadPreparedActions: boolean;

  public constructor(params: AssistantConversationParams) {
    this.conversationId = params.conversationId;
    this.startedAt = params.startedAt;
    this.lastMessageAt = params.lastMessageAt;
    this.turns = params.turns;
    this.hadPreparedActions = params.hadPreparedActions ?? false;
  }

  /**
   * The conversation as the transcript the panel renders.
   *
   * Two messages per exchange, in the order they happened, which is the same shape the panel
   * builds as a user sends messages - so a hydrated conversation and a live one are one list and
   * everything downstream of it stays unchanged.
   *
   * Every recorded turn was answered, so each assistant message is `Answered`. A failed turn is
   * never recorded: the server records a turn only once it has an answer to record.
   */
  public toTranscript(): AssistantResponse[] {
    const ordered = this.turns
      .slice()
      .sort((left, right) => left.sequence - right.sequence);

    return ordered.flatMap((turn, index) => [
      AssistantResponse.create({
        conversationId: this.conversationId,
        role: Role.User,
        text: turn.userMessage,
        citations: [],
        timestamp: turn.createdAt,
      }),
      AssistantResponse.create({
        conversationId: this.conversationId,
        role: Role.Assistant,
        text: turn.answerText,
        citations: turn.citations.map((citation) => AssistantCitation.create(citation)),
        timestamp: turn.createdAt,
        status: AssistantResponseStatus.Answered,
        // Prepared actions are not replayed. A staged action expires, and one that has not
        // would be re-offered here with no way for the user to know which turn's context it
        // belonged to - so a scrolled-back conversation shows what was said, not what is
        // still confirmable.
        stagedActions: [],
        // What the answers still say, though, is "confirm it below" - and for a while nothing
        // said otherwise, so a replayed transcript instructed the reader to use a control that
        // had been removed on purpose. The note goes on the last replayed turn rather than on
        // each one: the server records which conversation staged something, not which turn, and
        // the end of the transcript is where the reader stops looking for the missing card.
        preparedActionsNotReplayed:
          this.hadPreparedActions && index === ordered.length - 1,
      }),
    ]);
  }
}
